import React from "react";
import Text from "@/components/ui/text";
import { Button } from "@/components/ui/button";
import { IconButton } from "@/components/ui/icon-button";
import { Close, InfoCircle } from "@dreamhub/designsystem/icons";
import IconContainer from "@/components/ui/icon-container";

export interface DreamerMatch {
  id: string;
  title: string;
  subtitle?: string;
}

interface Props {
  /** Header line, already pluralized by the caller. */
  title: string;
  matches: DreamerMatch[];
  /** Label on each row's open-entity link. */
  openLabel: string;
  /** Accessible name for the icon-only dismiss button. */
  dismissLabel: string;
  /** Row glyph — Organization for companies, Person for people/leads. */
  matchIcon: React.ReactNode;
  onDismiss: () => void;
  onOpen: (id: string) => void;
  dataTest?: string;
}

/** Advisory "similar records already exist" box under a Dreamer field — dismissable, never blocking. */
const DreamerMatchList: React.FC<Props> = ({
  title,
  matches,
  openLabel,
  dismissLabel,
  matchIcon,
  onDismiss,
  onOpen,
  dataTest
}) => (
  <div
    className="mt-2 overflow-hidden rounded-lg border border-border-dreamer-card bg-surface-dreamer-card"
    data-test={dataTest}
  >
    <div className="flex min-h-8 items-center justify-between gap-3 px-3 py-2">
      <div className="flex min-w-0 items-center gap-2">
        <IconContainer
          as="span"
          color="natural"
          nativeStroke
          className="shrink-0"
        >
          <InfoCircle />
        </IconContainer>
        <Text variant="bodySecondary" className="leading-5 text-primary">
          {title}
        </Text>
      </div>
      <IconButton
        type="button"
        size="xs"
        variant="iconNoBg"
        className="shrink-0 text-secondary"
        aria-label={dismissLabel}
        onClick={onDismiss}
        data-test={dataTest ? `${dataTest}-dismiss` : undefined}
      >
        <Close />
      </IconButton>
    </div>
    <div className="flex flex-col gap-1.5 px-2 pb-2">
      {matches.map((match) => (
        <div
          key={match.id}
          className="flex min-h-10 items-center justify-between gap-3 rounded-md bg-surface-dreamer-card px-3 py-2"
        >
          <div className="flex min-w-0 items-center gap-2.5">
            <span className="flex size-5 shrink-0 items-center justify-center text-secondary">
              {matchIcon}
            </span>
            <div className="min-w-0">
              <Text variant="body" className="truncate">
                {match.title}
              </Text>
              {match.subtitle ? (
                <Text
                  variant="smBodySecondary"
                  className="block truncate text-secondary"
                >
                  {match.subtitle}
                </Text>
              ) : null}
            </div>
          </div>
          <Button
            type="button"
            variant="link"
            className="h-auto shrink-0 text-natural opacity-90 hover:opacity-100"
            onClick={() => onOpen(match.id)}
          >
            {openLabel}
          </Button>
        </div>
      ))}
    </div>
  </div>
);

export default DreamerMatchList;
